import useSaveFilter from '@op/hooks/useSaveFilter';
import { gameInfoType } from '@op/types/common';
import React, { useEffect, useMemo, useState } from 'react';
import SvgIcon from '@home/components/SvgIcon';
import {
  Button,
  Justify,
  List,
  Modal,
  SearchBox,
  StatusTip,
} from 'tea-component';
import BubbleCommon from '@op/components/BubbleCommon';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';
import ChannelAccountItem from '../ChannelAccountItem';
import {
  AccountChildrenOption,
  AccountOption,
  getAccountList,
} from '../../../utils';
import { NoDataCard } from '@op/components/NoDataCommon';
import _ from 'lodash-es';

export default function FilterList({
  gameInfo,
  options,
  selectFilter,
  setSelectFilter,
  handleSelectionChange,
  close,
  selectedAccounts,
}: {
  gameInfo: gameInfoType;
  options: AccountOption[];
  selectFilter: { text?: string; value?: number };
  setSelectFilter: (value: { text?: string; value?: number }) => void;
  handleSelectionChange: (accounts: string[], flag: boolean) => void;
  close: () => void;
  selectedAccounts: string[];
}) {
  const { isEn, formatMessage } = useLanguage();
  const {
    loading,
    filterList,
    deleteFilter,
    visible,
    closeDeleteWindow,
    openDeleteWindow,
  } = useSaveFilter({
    gameInfo,
    businessType: 3,
    needRequest: true,
  });

  const [searchText, setSearchText] = useState('');

  const allRenderList = useMemo(() => {
    const list = filterList.map((item) => {
      try {
        const accountList = JSON.parse(item.filter_info);
        const list = getAccountList(
          options,
          accountList?.social_media_by_account || [],
        );
        return {
          text: item.filter_name,
          value: item.filter_id,
          accounts: list,
          accountList: accountList?.social_media_by_account || [],
          showAccounts: list.slice(0, 5),
          showMoreNumber: list.length - 5,
        };
      } catch (error) {
        console.log('解析错误');
        return {
          text: item.filter_name,
          value: item.filter_id,
          accountList: [],
          accounts: getAccountList(options, []),
          showAccounts: [],
          showMoreNumber: 0,
        };
      }
    });

    return list;
  }, [filterList]);

  const renderList = useMemo(() => {
    const realText = searchText.toLocaleLowerCase();
    const list = allRenderList.filter((item) => {
      const name = item.text.toLocaleLowerCase();
      return name.includes(realText);
    });
    return list;
  }, [allRenderList, searchText]);

  function renderShowAccount(data: {
    text: string;
    value: number;
    accounts: AccountChildrenOption[];
    showAccounts: AccountChildrenOption[];
    showMoreNumber: number;
  }) {
    const { showAccounts = [], showMoreNumber, accounts } = data || {};
    if (showAccounts.length === 0) {
      return null;
    }

    const element = (
      <div className="dbo-show-account-box">
        {showAccounts.map((item) => (
          <div className="dbo-show-account-item" key={item.value}>
            <ChannelAccountItem item={item}></ChannelAccountItem>
          </div>
        ))}
        {showMoreNumber > 0 && <Button>+{showMoreNumber}</Button>}
      </div>
    );

    if (showMoreNumber <= 0) {
      return element;
    }

    return (
      <BubbleCommon
        tipsMap={undefined}
        showIcon={false}
        placement="left"
        className="dbo-bubble-account-wrapper"
        content={
          <div className="dbo-show-account-box">
            {accounts.map((item) => (
              <div className="dbo-show-account-item" key={item.value}>
                <ChannelAccountItem item={item}></ChannelAccountItem>
              </div>
            ))}
          </div>
        }
      >
        {element}
      </BubbleCommon>
    );
  }

  // 新版本
  // Segment切换到配置选项时, 选中的官号列表是否有对应的配置选项
  useEffect(() => {
    if (selectFilter.text || !selectedAccounts?.length) return;

    const matchedFilter = allRenderList.find((item) => {
      const arr1 = item.accountList;
      const arr2 = selectedAccounts;

      return arr1.length === arr2.length && _.xor(arr1, arr2).length === 0;
    });

    if (matchedFilter?.text && matchedFilter?.value) {
      setSelectFilter({
        text: matchedFilter.text,
        value: matchedFilter.value,
      });
    }
  }, [allRenderList]);

  return (
    <div className="dbo-filter-list-wrapper">
      <SearchBox
        onChange={(value) => setSearchText(value)}
        onSearch={(value = '') => setSearchText(value)}
        onClear={() => setSearchText('')}
        placeholder={formatMessage("Search")}
      />
      <List className="dbo-filter-list-box">
        {loading ? (
          <div className="dbo-filter-loading-box">
            <StatusTip
              loadingText={isEn ? 'Loading...' : '加载中...'}
              status={'loading'}
            />
          </div>
        ) : (
          <NoDataCard
            contentCard
            empty={renderList.length === 0}
            hasImg={false}
            hasDesc={false}
            height={80}
          >
            {renderList.map((item) => {
              const isCurrent = selectFilter?.value === item.value;
              return (
                <List.Item
                  className={`dbo-filter-item  ${
                    isCurrent ? '--dbo-current' : ''
                  }`}
                  key={item.value}
                  onClick={() => {
                    handleSelectionChange(item.accountList, true);
                    setSelectFilter({ text: item.text, value: item.value });
                    close();
                  }}
                >
                  <Justify
                    left={<div className={`dbo-filter-name`}>{item.text}</div>}
                    right={
                      <SvgIcon
                        type="trash-can-icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteWindow(item);
                        }}
                        className="dbo-filter-item-delete"
                      />
                    }
                  ></Justify>

                  {renderShowAccount(item)}
                </List.Item>
              );
            })}
          </NoDataCard>
        )}
      </List>

      <Modal visible={visible} onClose={() => closeDeleteWindow()}>
        <Modal.Body>
          <div className="dbo-history-deleteSetting-title">
            {formatMessage({
              id: 'Delete the filter setting',
            })}
          </div>
          <div className="dbo-history-deleteSetting-desc">
            {formatMessage({
              id: 'Do you want to delete?',
            })}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="primary"
            loading={loading}
            onClick={() => void deleteFilter(true)}
          >
            {formatMessage({ id: 'Confirm' })}
          </Button>
          <Button type="weak" onClick={() => closeDeleteWindow()}>
            {formatMessage({ id: 'Cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
